import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  MuiPickersUtilsProvider,
  Paper,
  TextField,
} from '@dls/react-core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DateFnsUtils from '@date-io/date-fns';
import { Icon } from '@dls/react-icon';
import { Grid } from '@material-ui/core';
import { GridFormElement } from '../form-controls';
import { getMeasurementTypes, MeasurementType, Subject } from '../../api/studykit';
import { StudyPageContext } from '../../App';
import {
  StyledDialogHeading,
  StyledDialogIconContainer,
  StyledDialogTitleForIcon,
} from '../styled/styles';
import { ColorX11 } from '../../styling/color';
import { DeleteDataConfirmDialog } from './delete-data-confirm-dialog';

interface ExportDataProps {
  open: boolean;
  onClose: () => void;
  subject: Subject;
}

export const DeleteDataDialog: React.FC<ExportDataProps> = ({ open, onClose, subject }) => {
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [measurementTypes, setMeasurementTypes] = useState<MeasurementType[]>([]);
  const [selectedMeasurements, setSelectedMeasurements] = useState<MeasurementType[]>([]);
  const { study } = useContext(StudyPageContext);

  useEffect(() => {
    getMeasurementTypes(study.id, subject.id).then(setMeasurementTypes);
  }, [study.id, subject.id]);

  const resetAndClose = () => {
    setConfirmDialogOpen(false);
    setSelectedMeasurements([]);
    onClose();
  };

  const handleDeleteButtonClick = async () => {
    setConfirmDialogOpen(true);
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      PaperProps={{ style: { width: `50vw`, maxWidth: `600px` } }}
    >
      <DeleteDataConfirmDialog
        subject={subject}
        open={isConfirmDialogOpen}
        measurements={selectedMeasurements}
        onClose={resetAndClose}
      />
      <StyledDialogTitleForIcon disableTypography>
        <StyledDialogIconContainer>
          <Icon name={'DeleteTrash'} size={'m'} color={ColorX11.Black} />
        </StyledDialogIconContainer>
        <StyledDialogHeading variant={'h3'}>Delete {subject.name}'s data</StyledDialogHeading>
      </StyledDialogTitleForIcon>
      <DialogContent>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Grid container spacing={3}>
            <GridFormElement xs={12} md={12} label={'Select Metrics'}>
              <Autocomplete
                multiple
                PaperComponent={Paper}
                classes={{
                  paper: 'DLS-Autocomplete',
                }}
                renderTags={(value: MeasurementType[], getTagProps) =>
                  value.map((option: MeasurementType, index: number) => (
                    <Chip variant="outlined" label={option.label} {...getTagProps({ index })} />
                  ))
                }
                getOptionLabel={(m) => m.label}
                options={measurementTypes}
                onChange={(_, v: MeasurementType[]) => setSelectedMeasurements([...v])}
                disableClearable
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />
            </GridFormElement>
            <Divider orientation={'horizontal'} />
          </Grid>
        </MuiPickersUtilsProvider>
      </DialogContent>
      <Divider orientation={'horizontal'} />
      <DialogActions style={{ justifyContent: `space-between` }}>
        <Button style={{ width: `8rem` }} onClick={resetAndClose} dls_variant={'quietDefault'}>
          Cancel
        </Button>
        <Button
          disabled={!selectedMeasurements.length}
          onClick={handleDeleteButtonClick}
          dls_variant={'signalError'}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
